import { IPromise } from 'angular'
import React, { useState } from 'react'
import VendNumber, { vn } from 'vend-number'

import { useVdTracking } from '../../../ng-compat/hooks'
import { $q } from '../../../ng-compat/imports'
import { Pay } from './Modals/Pay/Pay'
import { Change } from './Modals/Change/Change'
import { Refund } from './Modals/Refund/Refund'

export type HandlePaymentFunction = (
  paid: VendNumber,
  method: 'manual_enter' | 'manual_click' | 'suggestion'
) => void
export interface RoundedNumber {
  rounded: VendNumber | string
  remainder: VendNumber | string
}

export interface CashPaymentInformation {
  remainder: string
  cashAmount: string
  changeAmount?: string
}

interface CashPaymentModalProps {
  amountToPay: RoundedNumber
  balanceRemainder: VendNumber | string
  close: (val: IPromise<CashPaymentInformation>) => void
  currencySymbol: string
  isPartialPayment: boolean
  openCashDrawer: () => void
  rounding: VendNumber
}

export const CashPaymentModal: React.FC<CashPaymentModalProps> = ({
  amountToPay,
  balanceRemainder,
  close,
  currencySymbol,
  isPartialPayment,
  openCashDrawer,
  rounding,
}) => {
  // Show the partial change modal?
  const [partialChangeModal, setPartialChangeModal] = useState({
    display: false,
    paid: vn(0),
  })

  const vdTracking = useVdTracking()

  // Catch cases where reactular would pass `undefined`
  if (
    amountToPay === undefined ||
    balanceRemainder === undefined ||
    close === undefined ||
    currencySymbol === undefined ||
    isPartialPayment === undefined ||
    openCashDrawer === undefined ||
    rounding === undefined
  ) {
    return null
  }

  // Handle payments, show the change modal if it's a partial payment
  const handlePayment: HandlePaymentFunction = (paid, method) => {
    if (isPartialPayment && paid.gt(amountToPay.rounded)) {
      // Show the partial change modal
      setPartialChangeModal({
        display: true,
        paid,
      })
      // Open the cash drawer
      openCashDrawer()
    } else {
      pay(paid)
    }

    // Fire UBE
    vdTracking.trackUserEvent({
      eventName: 'Cash Modal Payment Handled',
      metadata: {
        amountTendered: vn(amountToPay.rounded).toNumber(),
        amountPaid: paid.toNumber(),
        isFinalPayment: !isPartialPayment,
        method,
      },
    })
  }

  // Actually Pay
  const pay = (paid: VendNumber): void => {
    const amountOwed = vn(amountToPay.rounded)
    let changeAmount: string | undefined

    if (isPartialPayment && partialChangeModal.paid.gt(amountOwed)) {
      changeAmount = partialChangeModal.paid.minus(amountOwed).toString()
    }

    if (!isPartialPayment && paid.gt(amountOwed)) {
      changeAmount = paid.minus(amountOwed).toString()
    }

    close(
      $q.resolve({
        // If it's the final payment we need to apply the cash rounding remainder of the balance
        remainder: isPartialPayment ? '0' : vn(balanceRemainder).toString(),
        cashAmount: paid.toString(),
        changeAmount,
      })
    )
  }

  if (vn(amountToPay.rounded).lt(0)) {
    // Open cash drawer
    openCashDrawer()

    return (
      <Refund
        amountToPay={amountToPay}
        isPartialPayment={isPartialPayment}
        closeWithoutPayment={() => close($q.reject())}
        pay={pay}
      />
    )
  }
  if (partialChangeModal.display && partialChangeModal.paid) {
    return (
      <Change
        amountToPay={amountToPay}
        paid={partialChangeModal.paid}
        pay={pay}
      />
    )
  } else {
    return (
      <Pay
        amountToPay={amountToPay}
        closeWithoutPayment={() => close($q.reject())}
        currencySymbol={currencySymbol}
        handlePayment={handlePayment}
        rounding={rounding}
      />
    )
  }
}
